// Grouped sidebar navigation. Pulled out of <AppShell /> so the same nav
// can render in the desktop sidebar AND inside the mobile <Sheet> without
// drift.
//
// Geometry (alignment-by-construction):
//
//   Every interactive icon in the rail — nav rows, theme toggle, collapse
//   toggle, user avatar — sits inside a <RailSlot> primitive. The slot is
//   `--sidebar-slot-w` wide (48px), with `--sidebar-row-h` height (36px),
//   and centers its child. The parent row is `mx-2` (8px gutter on each
//   side). Math:
//     • Rail 64px collapsed → row 48px wide → slot fills the row → icon
//       centered at 32px from rail edge.
//     • Rail 256px expanded → row 240px wide → slot is the leftmost 48px
//       → icon still at 32px from rail edge.
//   The icon column is invariant across both states by construction. Any
//   row that doesn't use <RailSlot> drifts and is a bug. This file is the
//   only place the slot is defined; the app-shell consumes it for the
//   footer rows so brand → nav → theme → user share one geometry.
//
// Active-state visual hierarchy:
//
//   The `data-active="true"` selector triggers four cues simultaneously:
//     • bg-accent on the row (the only persistent background among states)
//     • text-foreground + font-medium on the label
//     • text-primary tint on the icon (the *only* cue that survives in the
//       collapsed rail where the label is hidden)
//     • A 3px-wide bg-primary left bar inside the row's left margin.
//   Hover differs categorically (bg-accent/40, no icon tint, no bar) so
//   "I'm hovering" never reads as "I'm here".

import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  BarChart3,
  BookOpen,
  Bot,
  ExternalLink,
  KeyRound,
  LifeBuoy,
  MessagesSquare,
  Receipt,
  ScrollText,
  type LucideIcon,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip.js';
import { Separator } from '../ui/separator.js';
import { cn } from '../../lib/utils.js';
import { env } from '../../env.js';
import { useApiClient } from '../../lib/providers.js';
import { usageApi, USAGE_QUERY_KEY } from '../../lib/api/usage.js';

// The single source of truth for the icon-column geometry. Every icon-bearing
// trigger in the rail (here AND in the app-shell footer) renders inside this
// primitive. Width/height come from CSS variables in index.css so a future
// rail-width tweak is one place. Exported for the app-shell footer rows.
export function RailSlot({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        'flex h-[var(--sidebar-row-h)] w-[var(--sidebar-slot-w)] shrink-0 items-center justify-center',
        className,
      )}
    >
      {children}
    </span>
  );
}

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  // External rows render as <a target="_blank"> with no active-state tracking
  // (the link leaves the SPA, so the highlight machinery doesn't apply).
  external?: boolean;
  // Plan-gated rows. When the resolved plan code is in this set, the row
  // renders disabled (opacity-50, no hover target, click prevented) with a
  // tooltip explaining the upgrade path. Computed at render time from the
  // shared USAGE_QUERY_KEY cache so we don't pay an extra round-trip.
  hideForPlans?: ReadonlySet<string>;
  // Tooltip suffix shown when the row is disabled. Required when hideForPlans
  // is set so the operator always sees a clear upgrade hint.
  disabledTooltip?: string;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const FREE_ONLY: ReadonlySet<string> = new Set(['free']);

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Build',
    items: [
      { to: '/', label: 'Home', icon: BarChart3, end: true },
      { to: '/agents', label: 'Agents', icon: Bot },
      { to: '/api-keys', label: 'API keys', icon: KeyRound },
    ],
  },
  {
    label: 'Operate',
    items: [
      { to: '/usage', label: 'Usage', icon: Activity },
      { to: '/conversations', label: 'Conversations', icon: MessagesSquare },
      { to: '/audit', label: 'Audit log', icon: ScrollText },
    ],
  },
  {
    label: 'Account',
    items: [{ to: '/billing', label: 'Billing', icon: Receipt }],
  },
  {
    label: 'Help',
    items: [
      { to: env.docsUrl, label: 'Docs', icon: BookOpen, external: true },
      {
        to: '/support',
        label: 'Support',
        icon: LifeBuoy,
        hideForPlans: FREE_ONLY,
        disabledTooltip: 'Upgrade to Starter to file tickets',
      },
    ],
  },
];

interface SidebarNavProps {
  onNavigate?: () => void;
  collapsed?: boolean;
}

export function SidebarNav({ onNavigate, collapsed = false }: SidebarNavProps) {
  // Plan code drives plan-gated rows (Support today; future paid-only items
  // follow the same pattern). Reads from the shared USAGE_QUERY_KEY cache
  // populated by Home/Billing/Support — no extra round-trip on most loads.
  // Treat undefined (loading) as gating-on so paid rows don't briefly render
  // active before the plan check resolves.
  const api = useApiClient();
  const usage = useQuery({
    queryKey: USAGE_QUERY_KEY,
    queryFn: () => usageApi.summary(api),
  });
  const planCode = usage.data?.plan.code ?? 'free';

  return (
    <nav className="flex flex-col gap-3 py-3" aria-label="Primary">
      {NAV_GROUPS.map((group, groupIdx) => (
        <div key={group.label} className="flex flex-col gap-0.5">
          {/* Group separation:
              • Expanded — render the uppercase label (BUILD / OPERATE / ACCOUNT)
                in muted-foreground, aligned with the icon's left edge via pl-5.
              • Collapsed — replace the label with a thin Separator (dropped on
                the first group; the gap-3 on the parent <nav> handles spacing
                between groups). Reads as intentional structure rather than
                "compressed text". */}
          {collapsed ? (
            groupIdx > 0 && <Separator className="mx-3 mb-1" aria-hidden />
          ) : (
            <p className="pb-1 pl-5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {group.label}
            </p>
          )}
          {group.items.map((item) => {
            const disabled = item.hideForPlans?.has(planCode) ?? false;
            return item.external ? (
              <ExternalNavRow
                key={item.to}
                href={item.to}
                label={item.label}
                Icon={item.icon}
                collapsed={collapsed}
                onNavigate={onNavigate}
              />
            ) : (
              <NavRow
                key={item.to}
                to={item.to}
                label={item.label}
                Icon={item.icon}
                end={item.end ?? false}
                collapsed={collapsed}
                onNavigate={onNavigate}
                disabled={disabled}
                {...(item.disabledTooltip !== undefined && {
                  disabledTooltip: item.disabledTooltip,
                })}
              />
            );
          })}
        </div>
      ))}
    </nav>
  );
}

interface NavRowProps {
  to: string;
  label: string;
  Icon: LucideIcon;
  end: boolean;
  collapsed: boolean;
  onNavigate: (() => void) | undefined;
  // When true, render the row as a non-interactive div (no NavLink) with
  // dimmed opacity + tooltip. Click handlers + hover backgrounds are
  // suppressed so the row reads as "feature exists but locked" rather
  // than "broken link". Server-side gate is the actual enforcement; this
  // is UX-only.
  disabled?: boolean;
  disabledTooltip?: string;
}

function NavRow({
  to,
  label,
  Icon,
  end,
  collapsed,
  onNavigate,
  disabled = false,
  disabledTooltip,
}: NavRowProps) {
  // Disabled branch — render a static div mirroring the row geometry but
  // without NavLink (no navigation, no active-state machinery). Tooltip is
  // ALWAYS shown (collapsed or expanded) so the operator gets the upgrade
  // hint regardless of rail width. Cursor + opacity match disabled-button
  // semantics across the dashboard.
  if (disabled) {
    const row = (
      <div
        className={cn(
          'group relative mx-2 flex h-[var(--sidebar-row-h)] cursor-not-allowed items-center rounded-md text-sm opacity-50',
          'text-muted-foreground',
        )}
        aria-disabled="true"
      >
        <RailSlot>
          <Icon
            aria-hidden
            className="h-[var(--sidebar-icon-size)] w-[var(--sidebar-icon-size)]"
          />
        </RailSlot>
        <span className={cn('pr-3', collapsed ? 'sr-only' : 'whitespace-nowrap')}>
          {label}
        </span>
      </div>
    );
    return (
      <Tooltip>
        <TooltipTrigger asChild>{row}</TooltipTrigger>
        <TooltipContent side="right">
          {disabledTooltip ?? `${label} is not available on your plan`}
        </TooltipContent>
      </Tooltip>
    );
  }

  // Accessible-name strategy:
  //   • Expanded mode — the visible <span> below provides the name. Tooltip
  //     is suppressed (it would be redundant noise next to the visible label).
  //   • Collapsed mode — the <span> goes `sr-only` (out of layout, kept in
  //     the a11y tree). The wrapping <Tooltip> below provides hover
  //     discoverability for sighted users.
  // No aria-label on the link in expanded mode (would duplicate the visible
  // span). In collapsed mode the sr-only span IS the accessible name —
  // belt-and-braces aria-label was the previous code's choice and produced
  // the SR double-read.
  const link = (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          // Layout is identical in both states — the slot pattern keeps the
          // icon column at 32px from rail edge regardless of width. The rail
          // animates around the icon; the row never re-arranges.
          'group relative mx-2 flex h-[var(--sidebar-row-h)] items-center rounded-md text-sm transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card',
          isActive
            ? 'bg-accent text-foreground font-medium'
            : 'text-muted-foreground hover:bg-accent/40 hover:text-foreground',
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* 3px-wide left-bar accent in bg-primary. Sits in the row's left
              margin (the row is `mx-2`, so `-left-2` puts the bar flush with
              the rail edge). `rounded-r-full` makes it read as a notch
              extending out of the rail rather than a stripe. Renders in both
              states, opacity-toggled — so it's visible at the rail edge in
              collapsed mode too (the eye reads it as a tab on the rail). */}
          <span
            aria-hidden
            className={cn(
              'pointer-events-none absolute -left-2 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-primary transition-opacity duration-150',
              isActive ? 'opacity-100' : 'opacity-0',
            )}
          />
          <RailSlot>
            <Icon
              aria-hidden
              className={cn(
                'h-[var(--sidebar-icon-size)] w-[var(--sidebar-icon-size)] transition-all',
                isActive ? 'text-primary' : 'opacity-70 group-hover:opacity-100',
              )}
            />
          </RailSlot>
          <span
            className={cn(
              'pr-3',
              collapsed ? 'sr-only' : 'whitespace-nowrap',
            )}
          >
            {label}
          </span>
        </>
      )}
    </NavLink>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right">{label}</TooltipContent>
      </Tooltip>
    );
  }
  return link;
}

interface ExternalNavRowProps {
  href: string;
  label: string;
  Icon: LucideIcon;
  collapsed: boolean;
  onNavigate: (() => void) | undefined;
}

// Mirrors NavRow's geometry (RailSlot + label) but renders an <a> that opens
// in a new tab. No active-state machinery — the link leaves the SPA so there
// is no route to match against. The trailing ExternalLink glyph is only shown
// when expanded; in the collapsed rail, the tooltip suffix conveys the same.
function ExternalNavRow({ href, label, Icon, collapsed, onNavigate }: ExternalNavRowProps) {
  const link = (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onNavigate}
      className={cn(
        'group relative mx-2 flex h-[var(--sidebar-row-h)] items-center rounded-md text-sm transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card',
        'text-muted-foreground hover:bg-accent/40 hover:text-foreground',
      )}
    >
      <RailSlot>
        <Icon
          aria-hidden
          className="h-[var(--sidebar-icon-size)] w-[var(--sidebar-icon-size)] transition-colors"
        />
      </RailSlot>
      <span className={cn('flex-1', collapsed ? 'sr-only' : 'whitespace-nowrap')}>
        {label}
      </span>
      {!collapsed && (
        <ExternalLink
          aria-hidden
          className="mr-3 h-3.5 w-3.5 opacity-50 transition-opacity group-hover:opacity-80"
        />
      )}
    </a>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right">{label} (opens in new tab)</TooltipContent>
      </Tooltip>
    );
  }
  return link;
}
